import { isIPadCapacitor } from "@client/lib/isIPad";
import { trpc } from "@client/trpc";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * The tuned options for `trpc.getBuildId.useQuery()`. Shared with any other
 * mount of the same query (e.g. `PresenceRecorder`) so a second observer
 * doesn't register with React Query's default `staleTime` and refetch on its
 * own schedule next to this one.
 */
export const BUILD_ID_QUERY_OPTIONS = {
  staleTime: VERSION_POLL_INTERVAL_MS,
  refetchInterval: VERSION_POLL_INTERVAL_MS,
  refetchOnWindowFocus: true,
} as const;

type RepoCommitHashes = {
  repoCommitHash: string; // The commit hash of the whole repo
  engineCombinedCommitHash: string; // The combined commit hashes of engine/round and engine/core.ts
};

export function OutOfDateVersionWarning() {
  // FIXME: After we load, we ask the server what the latest version is, and
  // assume that must be our own version number. But that means that if we
  // somehow got a stale cached page, or are unlucky enough to load during the
  // window where the version is changing, we could think we're on a new version
  // when we're not.
  const hasDeliveredMessage = useRef(false);
  const { repoIsOutOfDate } = useIsOutOfDate();

  useEffect(() => {
    if (!repoIsOutOfDate) {
      return;
    }

    // The native iPad shell ships its own bundled JS, so reloading the page
    // can't pick up the new version — the refresh prompt is unactionable there.
    if (isIPadCapacitor()) {
      return;
    }

    // if the version has changed but we've already delivered the message, do nothing
    if (hasDeliveredMessage.current) {
      return;
    }

    // the version has changed and the user has not seen the message yet
    hasDeliveredMessage.current = true;
    toast.info(
      <div>
        There is a new version of this app. Please{" "}
        <button
          type="button"
          className="underline cursor-pointer"
          onClick={() => window.location.reload()}
        >
          refresh
        </button>{" "}
        the page to get the latest version.
      </div>,
      {
        duration: Number.POSITIVE_INFINITY,
      },
    );
  }, [repoIsOutOfDate]);

  return <></>;
}

export function useIsOutOfDate(): {
  repoIsOutOfDate: boolean;
  engineIsOutOfDate: boolean;
} {
  const initialVersion = useRef<RepoCommitHashes | null>(null);
  const [repoIsOutOfDate, setRepoIsOutOfDate] = useState(false);
  const [engineIsOutOfDate, setEngineIsOutOfDate] = useState(false);

  // Route the version check through React Query so every useIsOutOfDate consumer
  // (this hook is mounted by both the global warning and the round shell) shares
  // a single cached request instead of each running its own uncached poller and
  // firing duplicate getBuildId calls on load. The interval + window-focus
  // refetch replace the manual setInterval/visibilitychange wiring.
  const { data: buildId } = trpc.getBuildId.useQuery(
    undefined,
    BUILD_ID_QUERY_OPTIONS,
  );

  useEffect(() => {
    // request hasn't resolved yet, do nothing
    if (!buildId) {
      return;
    }

    const { repoCommitHash: currentVersion, engineCombinedCommitHash } =
      buildId;

    // first successful request, set the initial version
    if (initialVersion.current == null) {
      initialVersion.current = buildId;
      return;
    }

    if (initialVersion.current.repoCommitHash !== currentVersion) {
      setRepoIsOutOfDate(true);
    }

    if (
      initialVersion.current.engineCombinedCommitHash !==
      engineCombinedCommitHash
    ) {
      setEngineIsOutOfDate(true);
    }
  }, [buildId]);

  return {
    repoIsOutOfDate,
    engineIsOutOfDate,
  };
}
